import {Component, ElementRef, OnDestroy, OnInit} from "@angular/core";
import {FormBuilder} from "@angular/forms";
import {Observable} from "rxjs";
import {AddToCartControlBaseComponent} from "../../../shared/components/product/add-to-cart-control-base/add-to-cart-control-base.component";
import {AddMultipleFormatsToCartForm} from "../../../shared/forms/add-multiple-formats-to-cart.form";
import {AddMultipleFormatsToCartModel} from "../../../shared/models/cart/add-multiple-formats-to-cart.model";
import {AddToCartFormatModel} from "../../../shared/models/cart/add-to-cart-format.model";
import {AddToCartResultModel} from "../../../shared/models/cart/add-to-cart-result.model";
import {DeliveryRegionModel} from "../../../shared/models/cart/delivery-region.model";
import {PriceCalculationResultModel} from "../../../shared/models/cart/price-calculation-result.model";
import {ConditionListsAgreementModel } from "../../../shared/models/components/condition-lists-agreement.model";
import {ProductMtaAgreementModel } from "../../../shared/models/components/product-mta-agreement.model";
import {ProductFormatModel} from "../../../shared/models/product/product-format.model";
import {ConditionListsPopUpService } from "../../../shared/services/components/condition-lists-pop-up.service";
import {ProductMtaPopUpService } from "../../../shared/services/components/product-mta-pop-up.service";
import {NotificationService} from "../../../shared/services/components/notification.service";
import {ProductCartQuantityService } from "../../../shared/services/components/product-cart-quantity.service";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {LoggerService} from "../../../shared/services/logger.service";
import {ProductCartService} from "../../../shared/services/product/product-cart.service";
import {ProductService} from "../../../shared/services/product/product.service";
import {AddToCartWidgetConfigModel} from "../../models/add-to-cart-widget-config.model";
import {ProductModel} from "../../../shared/models/product/product.model";
import {DistributionRuleModel} from "../../../shared/models/cart/distribution-rule.model";
import {takeUntil} from "rxjs/operators";

@Component({
    selector: "pcg-ng-add-to-cart-widget",
    template: require('./add-to-cart-widget.component.html')
})
export class AddToCartWidgetComponent extends AddToCartControlBaseComponent<AddMultipleFormatsToCartForm, AddMultipleFormatsToCartModel> implements OnInit, OnDestroy {

    public product: ProductModel;

    public productFormats: Array<ProductFormatModel> = new Array<ProductFormatModel>();

    public deliveryCharge: string;
    public handlingCharge: string;

    public availableRegions: Array<DeliveryRegionModel> = new Array<DeliveryRegionModel>();

    public formatInfoUrl = "";

    public distributionRules: Array<DistributionRuleModel> = new Array<DistributionRuleModel>();

    public constructor(
        logger: LoggerService,
        formBuilder: FormBuilder,
        productCartService: ProductCartService,
        productService: ProductService,
        notificationService: NotificationService,
        productCartQuantityService: ProductCartQuantityService,
        private conditionListsPopUpService: ConditionListsPopUpService,
        private productMtaPopUpService: ProductMtaPopUpService,
        private domElement: ElementRef,
        private configService: ConfigurationService<AddToCartWidgetConfigModel>
    ) {
        super(formBuilder, productCartService, productService, notificationService, productCartQuantityService);
        this.configService.initialize(this.domElement.nativeElement.dataset);
    }

    public ngOnInit(): void {
        const config = this.configService.config;
        this.formatInfoUrl = config.formatInfoUrl;
        this.createSubscriptions();
    }

    public showAddToCartForm(): boolean {
        return !!this.addToCartForm
            && this.productAvailable
            && this.directDistributionRuleAvailable;
    }

    public get productAvailable(): boolean {
        return !this.product.discontinued && !this.product.accessioning;
    }

    public get directDistributionRuleAvailable(): boolean {
        return this.productAvailable && this.distributionRules.some(r => r.isDirectRule);
    }

    public get redirectRules(): Array<DistributionRuleModel> {
        return this.distributionRules.filter(r => !r.isDirectRule && !r.isDepositor);
    }

    public get depositorRules(): Array<DistributionRuleModel> {
        return this.distributionRules.filter(r => r.isDepositor);
    }

    public get indirectDistributionRules(): Array<DistributionRuleModel> {
        return  this.distributionRules.filter(r => !r.isDirectRule);
    }

    public canAddToCart(): boolean {
        return this.addToCartForm.formInstance.valid
            && this.addToCartForm.getModel().formats.find((x: AddToCartFormatModel) => x.quantity > 0)
            && !this.isLocked;
    }

    public handleSubmitClick(): void {
        this.lock();
        const productId = this.product.id;

        const localHandleListConditions = () => {
            this.productService.checkConditions([productId])
                .pipe(takeUntil(this.destroy$))
                .subscribe(checkResult => {
                    if (checkResult.length) {
                        this.conditionListsPopUpService.showPopUp(new ConditionListsAgreementModel (
                            this.product,
                            checkResult[0],
                            () => {
                                this.addToCartForm.submit();
                                this.conditionListsPopUpService.hidePopup();
                            },
                            () => this.conditionListsPopUpService.hidePopup()
                        ));
                    } else {
                        this.addToCartForm.submit();
                    }
                    this.unLock();
                });
        };
        this.productService.checkMta([productId])
            .pipe(takeUntil(this.destroy$))
            .subscribe(checkResults => {
                if (checkResults.length > 0) {
                    this.productMtaPopUpService.showPopUp(new ProductMtaAgreementModel (
                        this.product,
                        checkResults,
                        () => {
                            this.productMtaPopUpService.hidePopup();
                            localHandleListConditions();
                        },
                        () => {
                            this.productMtaPopUpService.hidePopup();
                            this.unLock();
                        }
                    ));
                } else {
                    localHandleListConditions();
                }
                this.unLock();
            });
    }

    protected createSubscriptions(): void {
        const productId = this.configService.config.productId;

        this.lock();
        this.productService.getProductById(productId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(product => {
                this.product = product;
                this.productFormats = product.formats;
                this.availableRegions = product.availableRegions;

                this.distributionRules = product.distributionRules;

                this.handlingCharge = product.handlingCharge;

                if(!this.product.accessioning) {
                    this.addToCartForm = AddMultipleFormatsToCartForm.createForm(
                        this.formBuilder,
                        product.id,
                        product.formats,
                        null
                    );

                    this.addToCartForm.changeObservable
                        .subscribe(model => this.handleChange(model));
                    this.addToCartForm.submitObservable
                        .subscribe(model => this.handleSubmit(model));
                }
                this.unLock();
            });
    }

    protected calculatePrice(model: AddMultipleFormatsToCartModel): Observable<PriceCalculationResultModel> {
        return this.productCartService.calculatePriceByMultipleFormats(model);
    }

    protected processCalculationResult(result: PriceCalculationResultModel): void {
        super.processCalculationResult(result);
        this.deliveryCharge = result.deliveryCharge;
        this.handlingCharge = result.handlingCharge;
        this.productFormats = result.formats;
        this.addToCartForm.handleFormatsUpdate(result.formats);
    }

    protected addProductToCart(model: AddMultipleFormatsToCartModel): Observable<AddToCartResultModel> {
        return this.productCartService.addMultipleFormatsToCart(model);
    }
}
